/**
 * Workout — in-chat panel for the active gym session.
 *
 * Phase 1: shell + auto-mount + minimize + end. Read-only prescription
 * checklist; set logging still goes through the voice tools (which write
 * the same gym_exercise_sets table the panel reads). Subsequent phases
 * (logging, rest timer, body diagram) layer on top of this shell.
 *
 * Talks to /api/tools/gym/* directly — no LLM turns burned during the
 * flow. The component re-fetches the session payload on focus so voice-
 * logged sets show up when the user comes back to the panel.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  XIcon,
  CheckIcon,
  BarbellIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import './Workout.css';
import type { RenderComponentProps } from '../render-registry';
import { SetLogger } from './SetLogger';
import { RestTimer } from './RestTimer';
import { SessionDiagram } from './SessionDiagram';
import { SessionHeader } from './SessionHeader';
import { ExercisePicker } from './ExercisePicker';
import { SwipeRow } from './SwipeRow';
import type { ResumePayload, SetEntry } from './types';

function fmtElapsed(startedAt: number): string {
  const sec = Math.max(0, Math.floor(Date.now() / 1000 - startedAt));
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  return `${h}h ${String(rem).padStart(2, '0')}m`;
}

export function Workout({
  props,
  onDismiss,
  onComplete,
}: RenderComponentProps<ResumePayload>) {
  const [payload, setPayload] = useState<ResumePayload>(props);
  const [, setTick] = useState(0);
  const [ending, setEnding] = useState(false);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tick once a minute so the elapsed-time chip stays roughly fresh.
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const refetch = useCallback(async () => {
    try {
      const res = await fetch(`/api/tools/gym/session/${payload.sessionId}`);
      if (!res.ok) return;
      const json = (await res.json()) as { payload?: ResumePayload };
      if (json.payload) setPayload(json.payload);
    } catch {
      // network blip; leave stale data
    }
  }, [payload.sessionId]);

  // Refetch on focus + when the page becomes visible — picks up voice-logged
  // sets without us needing to poll.
  useEffect(() => {
    const onFocus = () => {
      void refetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refetch]);

  // Light 5s poll so voice-logged sets appear without focus events
  // (single-user, cheap; pauses while tab is hidden).
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refetch();
    }, 5000);
    return () => window.clearInterval(t);
  }, [refetch]);

  // Immediate refetch when the chat fires a voice tool that may have
  // mutated this session. Beats waiting up to 5s for the next poll
  // tick. Chat.tsx dispatches this on every successful /api/chat reply.
  useEffect(() => {
    const onMutation = () => {
      void refetch();
    };
    window.addEventListener('bilbo:gym-mutation', onMutation);
    return () => window.removeEventListener('bilbo:gym-mutation', onMutation);
  }, [refetch]);

  const setActive = useCallback(
    async (exerciseId: string) => {
      try {
        const res = await fetch(
          `/api/tools/gym/session/${payload.sessionId}/active-exercise`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ exerciseId }),
          },
        );
        const json = (await res.json().catch(() => null)) as {
          payload?: ResumePayload;
        } | null;
        if (res.ok && json?.payload) setPayload(json.payload);
      } catch {
        // network blip; user can retry
      }
    },
    [payload.sessionId],
  );

  const handleLogged = useCallback((next: unknown) => {
    if (next && typeof next === 'object') setPayload(next as ResumePayload);
  }, []);

  const skipExercise = useCallback(
    async (exerciseId: string) => {
      try {
        const res = await fetch(
          `/api/tools/gym/session/${payload.sessionId}/skip`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ exerciseId }),
          },
        );
        const json = (await res.json().catch(() => null)) as {
          payload?: ResumePayload;
        } | null;
        if (res.ok && json?.payload) setPayload(json.payload);
      } catch {
        // network blip
      }
    },
    [payload.sessionId],
  );

  const undoSet = useCallback(
    async (setId: number) => {
      // Optimistic: remove the row locally so the panel reacts instantly.
      // Snapshot the pre-delete sets so we can restore on failure.
      const snapshot = payload.setsLogged;
      setPayload((p) => ({
        ...p,
        setsLogged: p.setsLogged.filter((s) => s.id !== setId),
      }));
      setError(null);
      try {
        const res = await fetch(
          `/api/tools/gym/session/${payload.sessionId}/sets/${setId}`,
          { method: 'DELETE' },
        );
        const json = (await res.json().catch(() => null)) as {
          payload?: ResumePayload;
          error?: string;
        } | null;
        if (res.ok && json?.payload) {
          setPayload(json.payload);
          return;
        }
        // Server rejected — put the row back and surface the reason so the
        // silent-failure trap (tap-with-no-visible-effect) can't happen.
        setPayload((p) => ({ ...p, setsLogged: snapshot }));
        setError(
          json?.error
            ? `couldn't remove set (${json.error})`
            : `couldn't remove set (http ${res.status})`,
        );
      } catch (e) {
        setPayload((p) => ({ ...p, setsLogged: snapshot }));
        setError(
          e instanceof Error ? `couldn't remove set (${e.message})` : "couldn't remove set",
        );
      }
    },
    [payload.sessionId, payload.setsLogged],
  );

  const [pickerOpenFor, setPickerOpenFor] = useState<string | null>(null);
  const handlePicked = useCallback(
    (idOrFreeform: string) => {
      void setActive(idOrFreeform);
    },
    [setActive],
  );

  // Per-exercise most-recent set id — drives which freeform/active row gets
  // the swipe-to-undo affordance.
  const lastSetIdByExercise = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of payload.setsLogged) m.set(s.exerciseId, s.id);
    return m;
  }, [payload.setsLogged]);

  const totalLogged = payload.setsLogged.length;

  async function finishSession(path: 'end' | 'discard') {
    if (ending) return;
    setEnding(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/tools/gym/session/${payload.sessionId}/${path}`,
        { method: 'POST' },
      );
      const json = (await res.json().catch(() => null)) as {
        wrapUp?: string;
        error?: string;
      } | null;
      if (!res.ok || !json?.wrapUp) {
        const verb = path === 'discard' ? 'discard' : 'end';
        setError(
          json?.error
            ? `couldn't ${verb} the session (${json.error})`
            : `couldn't ${verb} the session (http ${res.status})`,
        );
        setEnding(false);
        return;
      }
      onComplete(json.wrapUp);
    } catch (e) {
      const verb = path === 'discard' ? 'discard' : 'end';
      setError(
        e instanceof Error
          ? `couldn't ${verb} the session (${e.message})`
          : `couldn't ${verb} the session`,
      );
      setEnding(false);
    }
  }

  function handleEnd() {
    if (totalLogged === 0) {
      setEndConfirmOpen(true);
      return;
    }
    void finishSession('end');
  }

  const workingTotal = payload.setsLogged.filter((s) => !s.warmup).length;
  const warmupTotal = payload.setsLogged.filter((s) => s.warmup).length;
  const title = payload.mode === 'planned'
    ? (payload.dayLabel ?? 'Planned workout')
    : 'Freeform session';

  return (
    <div className="workout-panel">
      <header className="workout-header">
        <div className="workout-title-row">
          <BarbellIcon size={18} weight="regular" aria-hidden="true" />
          <span className="workout-title">{title}</span>
        </div>
        <div className="workout-meta">
          <span>{fmtElapsed(payload.startedAt)}</span>
          <span>·</span>
          <span>
            {workingTotal} working
            {warmupTotal > 0 ? ` + ${warmupTotal} warmup` : ''}
          </span>
        </div>
        <div className="workout-actions">
          <button
            type="button"
            className="workout-action workout-minimize"
            onClick={() => onDismiss()}
            aria-label="minimize"
          >
            <XIcon size={16} weight="regular" aria-hidden="true" />
            minimize
          </button>
          <button
            type="button"
            className="workout-action workout-end"
            onClick={handleEnd}
            disabled={ending}
            aria-label="end workout"
          >
            <CheckIcon size={16} weight="regular" aria-hidden="true" />
            {ending ? 'ending…' : 'end workout'}
          </button>
        </div>
      </header>

      {error && <p className="workout-error">⚠️ {error}</p>}

      {payload.mode === 'planned' && (
        <BodyweightPrompt
          sessionId={payload.sessionId}
          current={payload.bodyweightKg}
          onUpdated={setPayload}
        />
      )}

      <SessionSettings
        sessionId={payload.sessionId}
        restOverrideSec={payload.restOverrideSec}
        onUpdated={setPayload}
      />

      <SessionHeader
        setsCount={workingTotal}
        regionBalance={payload.regionBalance}
      />

      <SessionDiagram muscles={payload.muscleActivation} />

      {payload.mode === 'planned' && payload.prescription.length > 0 ? (
        <ol className="workout-prescription">
          {payload.prescription.map((p) => {
            const active = p.exerciseId === payload.activeExerciseId;
            const cls = [
              'workout-row',
              active ? 'workout-row-active' : '',
              p.completed ? 'workout-row-completed' : '',
              p.skipped ? 'workout-row-skipped' : '',
            ]
              .filter(Boolean)
              .join(' ');
            const targetTxt = p.targetSets && p.targetReps
              ? `${p.targetSets}×${p.targetReps}`
              : p.targetSets
                ? `${p.targetSets} sets`
                : p.targetReps ?? '';
            const weightTxt = p.targetWeightKg != null ? ` @ ${p.targetWeightKg} kg` : '';
            const lastSetIdForRow = lastSetIdByExercise.get(p.exerciseId) ?? null;
            return (
              <li key={p.exerciseId} className={cls}>
                <SwipeRow
                  actionLabel="skip"
                  actionClass="swipe-action-skip"
                  onAction={() => void skipExercise(p.exerciseId)}
                  disabled={p.skipped || p.completed}
                >
                  <button
                    type="button"
                    className="workout-row-tap"
                    onClick={() => void setActive(p.exerciseId)}
                    aria-label={`make ${p.name} active`}
                    aria-pressed={active}
                  >
                    <div className="workout-row-name workout-row-name-wrap">
                      <span>{p.name}</span>
                      <button
                        type="button"
                        className="workout-row-swap"
                        aria-label={`swap ${p.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPickerOpenFor(p.exerciseId);
                        }}
                      >
                        <PencilSimpleIcon size={12} weight="regular" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="workout-row-target">
                      {targetTxt}{weightTxt}
                    </div>
                    <div className="workout-row-progress">
                      {p.skipped
                        ? 'skipped'
                        : p.targetSets != null
                          ? `${p.setsDone}/${p.targetSets}`
                          : `${p.setsDone}`}
                    </div>
                  </button>
                </SwipeRow>
                {active && payload.activeExerciseId && (
                  <>
                    <RestTimer
                      lastSetAt={payload.lastSetAt}
                      restSec={payload.restOverrideSec ?? p.restSec}
                    />
                    <SetLogger
                      sessionId={payload.sessionId}
                      exerciseId={payload.activeExerciseId}
                      suggested={payload.suggestedNextSet}
                      onLogged={handleLogged}
                    />
                    {lastSetIdForRow != null && (
                      <SwipeRow
                        actionLabel="undo"
                        actionClass="swipe-action-undo"
                        onAction={() => void undoSet(lastSetIdForRow)}
                      >
                        <div className="workout-freeform-row">
                          <span>last set</span>
                          <span>swipe ← to undo</span>
                        </div>
                      </SwipeRow>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ol>
      ) : (
        <>
          {/* Freeform-only: active-exercise card. Without this the panel has
              no way to switch exercise; the prescription-list affordance
              that planned mode uses doesn't exist here. Tapping opens the
              same ExercisePicker the planned-mode swap uses. */}
          <button
            type="button"
            className="freeform-active-card"
            onClick={() => setPickerOpenFor('freeform')}
            aria-label={
              payload.activeExerciseId
                ? `change active exercise (currently ${payload.activeExerciseId})`
                : 'pick an exercise'
            }
          >
            <span className="freeform-active-label">active exercise</span>
            <span className="freeform-active-name">
              {payload.activeExerciseId ?? 'pick an exercise…'}
            </span>
            <span className="freeform-active-change">
              {payload.activeExerciseId ? 'change' : 'pick'}
            </span>
          </button>

          {payload.activeExerciseId && (
            <SetLogger
              sessionId={payload.sessionId}
              exerciseId={payload.activeExerciseId}
              suggested={payload.suggestedNextSet}
              onLogged={handleLogged}
            />
          )}
          <FreeformLog sets={payload.setsLogged} onUndo={undoSet} />
        </>
      )}

      <ExercisePicker
        open={pickerOpenFor != null}
        onClose={() => setPickerOpenFor(null)}
        onPick={handlePicked}
      />

      {endConfirmOpen && (
        <div
          className="workout-modal-backdrop"
          onClick={() => !ending && setEndConfirmOpen(false)}
        >
          <div
            className="workout-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="workout-modal-title">Nothing logged.</div>
            <div className="workout-modal-body">
              Discard this session?
            </div>
            <div className="workout-modal-actions">
              <button
                type="button"
                className="workout-action"
                onClick={() => setEndConfirmOpen(false)}
                disabled={ending}
              >
                keep
              </button>
              <button
                type="button"
                className="workout-action workout-end"
                onClick={() => void finishSession('discard')}
                disabled={ending}
              >
                {ending ? 'discarding…' : 'discard'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BodyweightPrompt({
  sessionId,
  current,
  onUpdated,
}: {
  sessionId: number;
  current: number | null;
  onUpdated: (p: ResumePayload) => void;
}) {
  const [dismissed, setDismissed] = useState(false);
  const [value, setValue] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  if (current != null || dismissed) return null;

  async function save() {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 30 || n > 250) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/tools/gym/session/${sessionId}/bodyweight`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bodyweightKg: n }),
      });
      const json = (await res.json().catch(() => null)) as {
        payload?: ResumePayload;
      } | null;
      if (res.ok && json?.payload) onUpdated(json.payload);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bodyweight-prompt">
      <span className="bodyweight-prompt-label">set bodyweight (kg)</span>
      <input
        type="number"
        inputMode="decimal"
        className="bodyweight-prompt-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        min={30}
        max={250}
        step={0.1}
      />
      <button
        type="button"
        className="bodyweight-prompt-save"
        onClick={() => void save()}
        disabled={submitting || !value.trim()}
      >
        save
      </button>
      <button
        type="button"
        className="bodyweight-prompt-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="dismiss"
      >
        ✕
      </button>
    </div>
  );
}

function SessionSettings({
  sessionId,
  restOverrideSec,
  onUpdated,
}: {
  sessionId: number;
  restOverrideSec: number | null;
  onUpdated: (p: ResumePayload) => void;
}) {
  const [value, setValue] = useState<number>(restOverrideSec ?? 120);
  useEffect(() => {
    if (restOverrideSec != null) setValue(restOverrideSec);
  }, [restOverrideSec]);

  async function patch(restSec: number | null) {
    try {
      const res = await fetch(
        `/api/tools/gym/session/${sessionId}/rest-override`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ restSec }),
        },
      );
      const json = (await res.json().catch(() => null)) as {
        payload?: ResumePayload;
      } | null;
      if (res.ok && json?.payload) onUpdated(json.payload);
    } catch {
      // ignore
    }
  }

  return (
    <details className="session-settings">
      <summary className="session-settings-summary">
        ⚙ session settings
        {restOverrideSec != null && ` · rest ${restOverrideSec}s`}
      </summary>
      <div className="session-settings-body">
        <div className="session-settings-row">
          <span className="session-settings-row-label">rest override (sec)</span>
          <button
            type="button"
            className="stepper-btn"
            onClick={() => setValue((v) => Math.max(30, v - 15))}
            aria-label="decrease rest"
          >
            −
          </button>
          <span className="set-logger-value">{value}</span>
          <button
            type="button"
            className="stepper-btn"
            onClick={() => setValue((v) => Math.min(600, v + 15))}
            aria-label="increase rest"
          >
            +
          </button>
          <button
            type="button"
            className="bodyweight-prompt-save"
            onClick={() => void patch(value)}
          >
            apply
          </button>
        </div>
        {restOverrideSec != null && (
          <div className="session-settings-row">
            <span className="session-settings-row-label">
              currently overriding to {restOverrideSec}s
            </span>
            <button
              type="button"
              className="session-settings-clear"
              onClick={() => void patch(null)}
            >
              clear
            </button>
          </div>
        )}
      </div>
    </details>
  );
}

function FreeformLog({
  sets,
  onUndo,
}: {
  sets: SetEntry[];
  onUndo: (setId: number) => void;
}) {
  if (sets.length === 0) {
    return <p className="workout-empty">No sets logged yet. Voice-log them — they'll appear here.</p>;
  }
  const grouped = new Map<string, SetEntry[]>();
  for (const s of sets) {
    const list = grouped.get(s.exerciseId);
    if (list) list.push(s);
    else grouped.set(s.exerciseId, [s]);
  }
  function renderSetText(s: SetEntry): string {
    const main =
      s.reps != null && s.weightKg != null
        ? `${s.reps} × ${s.weightKg} kg`
        : s.reps != null
          ? `${s.reps} reps`
          : '(no metric)';
    return `${main}${s.warmup ? ' (warmup)' : ''}${s.bfr ? ' (BFR)' : ''}`;
  }
  // Every row is individually removable now — swipe ← on any set, not just
  // the most recent. Server-side the DELETE endpoint allows any set in the
  // active session.
  return (
    <div className="workout-freeform">
      {[...grouped.entries()].map(([exerciseId, exSets]) => {
        return (
          <div key={exerciseId} className="workout-freeform-group">
            <div className="workout-freeform-name">{exerciseId}</div>
            <ul className="workout-freeform-sets">
              {exSets.map((s) => (
                <li key={s.id} style={{ listStyle: 'none' }}>
                  <SwipeRow
                    actionLabel="remove"
                    actionClass="swipe-action-undo"
                    onAction={() => onUndo(s.id)}
                  >
                    <div className="workout-freeform-row">
                      <span>{renderSetText(s)}</span>
                    </div>
                  </SwipeRow>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
